package com.progilift.pilot.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLProgressElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import kotlin.js.JSON
import kotlin.js.Date
import kotlin.js.Promise
import kotlin.js.unsafeCast

private var challengeId: String? = null
private var challengeDeliveryMode: String? = null
private var challengeRemainingAttempts = 3
private var currentCsrfToken: String? = null
private var currentPartition: String? = null
private var currentSession: dynamic = null
private var currentMaintenance: dynamic = null
private var pendingClaimId: String? = null
private var selectedClaimPending = false
private var forceOffline = false
private var syncing = false
private var pendingCount = 0
private var conflictCount = 0
private var mediaPendingCount = 0
private var syncStartedAt = 0.0
private val selectedOperations = mutableSetOf<String>()
private var adminMode = ""
private var adminCsrfToken: String? = null
private var adminTenantId: String? = null
private var adminRevision = 0
private var adminPolicies: dynamic = null

fun main() {
    val root = document.getElementById("app") ?: return
    val view = window.location.search.removePrefix("?").split('&').firstOrNull { it.startsWith("view=") }?.substringAfter('=')
    if (view == "administration" || view == "super-administration") {
        adminMode = if (view == "super-administration") "super-admin" else "company-admin"
        root.innerHTML = administrationMarkup(adminMode == "super-admin")
        bindAdministration()
        restoreAdministration()
        return
    }
    root.innerHTML = shellMarkup()
    bindEvents()
    restoreSession()
}

private fun offline(): dynamic = window.asDynamic().progiliftOffline

private fun bindEvents() {
    document.getElementById("login-button")?.addEventListener("click", { startChallenge() })
    document.getElementById("verify-button")?.addEventListener("click", { verifyChallenge() })
    document.getElementById("back-button")?.addEventListener("click", { showAuth("credentials") })
    document.getElementById("resend-button")?.addEventListener("click", { resendChallenge() })
    document.getElementById("logout-button")?.addEventListener("click", { logout() })
    document.getElementById("retry-button")?.addEventListener("click", { loadData() })
    document.getElementById("close-detail")?.addEventListener("click", { hideDetail() })
    document.getElementById("close-operational")?.addEventListener("click", { visible("operational-panel", false) })
    document.getElementById("missions-card")?.addEventListener("click", { openOperationalList("missions") })
    document.getElementById("incidents-card")?.addEventListener("click", { openOperationalList("incidents") })
    document.getElementById("breakdowns-card")?.addEventListener("click", { openOperationalList("breakdowns?category=all") })
    document.getElementById("supervision-entry")?.addEventListener("click", { openSupervision() })
    document.getElementById("offline-toggle")?.addEventListener("click", { toggleOffline() })
    document.getElementById("sync-button")?.addEventListener("click", { syncOutbox() })
    document.getElementById("claim-button")?.addEventListener("click", { queueClaim() })
    document.getElementById("draft-form")?.addEventListener("submit", { event -> event.preventDefault(); queueDraft() })
    document.getElementById("photo-input")?.addEventListener("change", { capturePhotos() })
    document.getElementById("sync-media-button")?.addEventListener("click", { syncSelectedMedia() })
    document.getElementById("save-signature-button")?.addEventListener("click", { captureSignature() })
    document.getElementById("clear-signature-button")?.addEventListener("click", { offline().clearSignature("signature-canvas") })
    document.getElementById("signature-consent")?.addEventListener("change", { (document.getElementById("save-signature-button") as? HTMLButtonElement)?.disabled = !input("signature-consent").checked })
    document.getElementById("complete-button")?.addEventListener("click", { queueCompletion() })
    document.getElementById("runtime-check")?.addEventListener("click", { checkRuntimeUpdate() })
    document.getElementById("runtime-persist")?.addEventListener("click", { requestPersistentStorage() })
    document.getElementById("runtime-activate")?.addEventListener("click", { activateRuntimeUpdate() })
    window.addEventListener("progilift:store-changed", { refreshOutbox(); refreshRuntime() })
    window.addEventListener("progilift:pwa-changed", { refreshRuntime() })
    textarea("public-note").addEventListener("input", { saveLocalDraft() })
    textarea("internal-note").addEventListener("input", { saveLocalDraft() })
    input("started-at").addEventListener("input", { saveLocalDraft() })
    input("ended-at").addEventListener("input", { saveLocalDraft() })
}

private fun restoreSession() {
    showOnly("loading-state")
    apiFetch("me").then<Any?> { response ->
        when {
            response.ok -> response.json().then<Any?> { session -> loadData(session); null }
            response.status.toInt() == 401 -> { showAuth("credentials"); refreshLockedSummary() }
            else -> showOnly("error-state")
        }
        null
    }.catch { showOnly("error-state"); null }
}

private fun startChallenge() {
    clearAuthError()
    val identifier = input("identifier").value.trim()
    val password = input("password").value
    if (identifier.isBlank() || password.isBlank()) { showAuthError("Renseignez votre identifiant et votre mot de passe."); return }
    submitting(true)
    val payload = js("({})")
    payload.identifier = identifier
    payload.password = password
    apiFetch("auth/challenges", "POST", JSON.stringify(payload)).then<Any?> { response ->
        if (response.ok) {
            response.json().then<Any?> { challenge ->
                val value = challenge.asDynamic()
                challengeId = value.challengeId as String
                challengeDeliveryMode = value.deliveryMode as String
                challengeRemainingAttempts = (value.remainingAttempts as Number).toInt()
                text("masked-destination", value.maskedDestination as String)
                text("remaining-attempts", remainingAttemptsText())
                visible("capture-hint", challengeDeliveryMode == "capture")
                input("mfa-code").value = ""
                showAuth("code")
                window.setTimeout({ (document.getElementById("resend-button") as? HTMLButtonElement)?.disabled = false }, 600_000)
                submitting(false)
                null
            }
        } else readProblem(response, "Impossible d’envoyer le code.").then<Any?> { message -> showAuthError(message); submitting(false); null }
        null
    }.catch { showAuthError("Le service de connexion est momentanément indisponible."); submitting(false); null }
}

private fun verifyChallenge() {
    clearAuthError()
    val id = challengeId ?: return
    val code = input("mfa-code").value.trim()
    if (code.length != 6 || code.any { !it.isDigit() }) { showAuthError("Saisissez le code à 6 chiffres reçu par e-mail."); return }
    submitting(true)
    val payload = js("({})")
    payload.code = code
    apiFetch("auth/challenges/$id/verification", "POST", JSON.stringify(payload)).then<Any?> { response ->
        if (response.ok) {
            response.json().then<Any?> { session -> challengeId = null; input("password").value = ""; visible("locked-work", false); loadData(session); submitting(false); null }
        } else {
            response.json().then<Any?> { problem ->
                val value = problem.asDynamic()
                challengeRemainingAttempts = ((value.remainingAttempts as? Number)?.toInt() ?: challengeRemainingAttempts)
                text("remaining-attempts", remainingAttemptsText())
                showAuthError((value.title as? String) ?: "Le code est invalide ou expiré.")
                submitting(false)
                null
            }
        }
        null
    }.catch { showAuthError("La vérification n’a pas pu être terminée."); submitting(false); null }
}

private fun resendChallenge() {
    val id = challengeId ?: return
    submitting(true); clearAuthError()
    apiFetch("auth/challenges/$id/resend", "POST").then<Any?> { response ->
        if (response.ok) response.json().then<Any?> { challenge ->
            challengeRemainingAttempts = (challenge.asDynamic().remainingAttempts as Number).toInt()
            text("remaining-attempts", remainingAttemptsText()); input("mfa-code").value = ""; submitting(false); null
        } else readProblem(response, "Le renvoi n’est pas encore disponible.").then<Any?> { message -> showAuthError(message); submitting(false); null }
        null
    }.catch { showAuthError("Le renvoi n’a pas pu être effectué."); submitting(false); null }
}

private fun logout() {
    if (pendingCount + conflictCount > 0 && !window.confirm("Votre travail local sera conservé mais verrouillé jusqu’à votre reconnexion. Continuer ?")) return
    val headers = Headers()
    currentCsrfToken?.let { headers.set("X-CSRF-Token", it) }
    apiFetch("auth/sessions/current", "DELETE", headers = headers).then<Any?> { lockAndShowLogin(); null }.catch { lockAndShowLogin(); null }
}

private fun lockAndShowLogin() {
    currentCsrfToken = null; currentPartition = null; currentSession = null; currentMaintenance = null; challengeId = null
    input("password").value = "Pilot!1"
    showAuth("credentials")
    refreshLockedSummary()
}

private fun refreshLockedSummary() {
    offline().lockedSummary().unsafeCast<Promise<dynamic>>().then<Any?> { summary ->
        val locked = summary.locked as Boolean
        visible("locked-work", locked)
        if (locked) text("locked-copy", "${(summary.drafts as Number).toInt()} brouillon(s) et ${(summary.pending as Number).toInt()} commande(s) restent verrouillés jusqu’à la reconnexion du même compte.")
        null
    }
}

private fun loadData(knownSession: dynamic = null) {
    showOnly("loading-state")
    val sessionPromise = if (knownSession == null) fetchJson("me") else Promise.resolve(knownSession)
    sessionPromise.then<Any?> { session ->
        fetchJson("maintenances/due?month=2026-08").then<Any?> { page -> renderReady(session, page); null }
        null
    }.catch { showOnly("error-state"); null }
}

private fun loadDetail(maintenanceId: String) {
    fetchJson("maintenances/$maintenanceId").then<Any?> { maintenance -> renderDetail(maintenance); null }
        .catch { visible("detail-error", true); null }
}

private fun renderReady(session: dynamic, page: dynamic) {
    val items = page.items.unsafeCast<Array<dynamic>>()
    val sectors = session.sectors.unsafeCast<Array<dynamic>>()
    currentSession = session
    currentCsrfToken = session.csrfToken as String
    currentPartition = offline().partition(session.tenant.id as String, session.user.id as String) as String
    text("hero-eyebrow", "Interventions du jour"); text("hero-title", "Bonjour, ${session.user.displayName as String}"); text("session-copy", "Session sécurisée · 30 jours")
    text("identity-initials", initials(session.user.displayName as String)); text("tenant-name", session.tenant.displayName as String); text("sector-list", sectors.joinToString(" · ") { it.label as String })
    text("maintenance-count", "${items.size} maintenances")
    visible("empty-state", items.isEmpty()); visible("maintenance-list", items.isNotEmpty())
    renderMaintenanceList(items)
    renderLoadMore(session, page)
    showOnly("ready-state")
    loadOperationalDashboard(session)
    refreshOutbox()
    refreshRuntime()
    offline().recordMetric(currentPartition, "client.session.ready", offline().navigationElapsed(), "Kotlin")
}

private fun loadOperationalDashboard(session: dynamic) {
    fetchJson("agenda?from=2026-09-25&to=2026-09-25").then<Any?> { renderAgenda(it); null }
        .catch { renderAgendaError(); null }
    fetchJson("breakdowns/summary").then<Any?> { renderBreakdownSummary(it); null }
        .catch { text("breakdowns-total", "—"); null }
    fetchJson("incidents/summary").then<Any?> { summary ->
        text("incidents-count", ((summary.active as Number).toInt()).toString()); null
    }.catch { text("incidents-count", "—"); null }
    fetchJson("missions/summary").then<Any?> { summary ->
        text("missions-count", ((summary.active as Number).toInt()).toString()); null
    }.catch { text("missions-count", "—"); null }
    val capabilities = session.capabilities.unsafeCast<Array<String>>()
    visible("supervision-entry", capabilities.contains("maintenance.supervise"))
}

private fun renderAgenda(page: dynamic) {
    val items = page.items.unsafeCast<Array<dynamic>>()
    val list = document.getElementById("agenda-list") as? HTMLElement ?: return
    list.innerHTML = ""
    items.forEach { item ->
        val category = item.category
        val row = element("li", "agenda-item")
        val time = element("time", "agenda-time", agendaTime(item.startAt as String))
        val marker = element("span", "agenda-marker").apply { setAttribute("style", "background:${category.color as String}") }
        val copy = element("span", "agenda-copy").apply {
            append(element("strong", text = item.title as String))
            append(element("small", text = (item.location as? String) ?: (item.description as? String) ?: category.label as String))
        }
        row.append(time, marker, copy)
        list.append(row)
    }
    visible("agenda-empty", items.isEmpty())
    visible("agenda-list", items.isNotEmpty())
}

private fun renderAgendaError() {
    visible("agenda-list", false); visible("agenda-empty", true)
    text("agenda-empty", "Agenda momentanément indisponible.")
}

private fun renderBreakdownSummary(summary: dynamic) {
    val toTake = (summary.toTake as Number).toInt()
    val toHandle = (summary.toHandle as Number).toInt()
    val inProgress = (summary.inProgress as Number).toInt()
    val stopped = (summary.stopped as Number).toInt()
    text("breakdowns-total", (toTake + toHandle + inProgress + stopped).toString())
    text("breakdowns-copy", "$toTake à prendre · $toHandle à traiter · $inProgress en cours · $stopped à l’arrêt")
}

private fun openOperationalList(path: String) {
    val title = when {
        path.startsWith("missions") -> "Missions"
        path.startsWith("incidents") -> "Incidents"
        else -> "Pannes"
    }
    text("operational-title", title)
    val list = document.getElementById("operational-list") as? HTMLElement ?: return
    list.innerHTML = "<li class=\"empty-state\">Chargement…</li>"
    visible("operational-panel", true)
    fetchJson(path).then<Any?> { page ->
        list.innerHTML = ""
        val items = page.items.unsafeCast<Array<dynamic>>()
        items.forEach { item ->
            val equipment = item.equipment
            val row = element("li", "operational-item")
            val copy = element("span", "operational-copy")
            val label = when (title) {
                "Missions" -> (item.categoryLabel as? String) ?: "Mission"
                "Incidents" -> "Mission Incident"
                else -> "Panne"
            }
            copy.append(
                element("strong", text = equipment.displayNumber as String),
                element("span", text = label),
                element("small", text = equipment.address as String)
            )
            row.append(copy, element("span", "state-badge ${stateClass(item.state as String)}", operationalState(item.state as String)))
            list.append(row)
        }
        if (items.isEmpty()) list.append(element("li", "empty-state", "Aucun élément dans votre périmètre."))
        null
    }.catch {
        list.innerHTML = ""; list.append(element("li", "empty-state", "La liste n’est pas disponible.")); null
    }
}

private fun openSupervision() {
    text("operational-title", "Supervision")
    val list = document.getElementById("operational-list") as? HTMLElement ?: return
    list.innerHTML = "<li class=\"empty-state\">Chargement de l’équipe…</li>"
    visible("operational-panel", true)
    fetchJson("supervision/technicians").then<Any?> { page ->
        list.innerHTML = ""
        val technicians = page.technicians.unsafeCast<Array<dynamic>>()
        technicians.forEach { technician ->
            val counts = technician.counts
            val row = element("button", "supervision-technician-row") as HTMLButtonElement
            row.type = "button"
            row.append(
                element("span", "avatar mini-avatar", initials(technician.displayName as String)),
                element("span", "operational-copy").apply {
                    append(
                        element("strong", text = technician.displayName as String),
                        element("span", text = "Secteurs " + technician.sectorIds.unsafeCast<Array<String>>().joinToString(" · ")),
                        element("small", text = "${(counts.toTake as Number).toInt()} à prendre · ${(counts.inProgress as Number).toInt()} en cours · ${(counts.stopped as Number).toInt()} à l’arrêt")
                    )
                },
                element("span", "state-badge in-progress", "${(counts.active as Number).toInt()} actives")
            )
            row.onclick = { openSupervisionBreakdowns(technician.id as String, technician.displayName as String); null }
            element("li").apply { append(row); list.append(this) }
        }
        if (technicians.isEmpty()) list.append(element("li", "empty-state", "Aucun technicien dans votre périmètre."))
        null
    }.catch {
        list.innerHTML = ""; list.append(element("li", "empty-state", "La supervision n’est pas disponible.")); null
    }
}

private fun openSupervisionBreakdowns(technicianId: String, displayName: String) {
    text("operational-title", displayName)
    val list = document.getElementById("operational-list") as? HTMLElement ?: return
    list.innerHTML = "<li class=\"empty-state\">Chargement des pannes…</li>"
    fetchJson("supervision/technicians/$technicianId/breakdowns?state=all").then<Any?> { page ->
        list.innerHTML = ""
        val items = page.items.unsafeCast<Array<dynamic>>()
        items.forEach { item ->
            val equipment = item.equipment
            val row = element("li", "operational-item")
            row.append(
                element("span", "operational-copy").apply {
                    append(
                        element("strong", text = equipment.displayNumber as String),
                        element("span", text = equipment.address as String),
                        element("small", text = agendaTime(item.reportedAt as String))
                    )
                },
                element("span", "state-badge ${stateClass(item.state as String)}", operationalState(item.state as String))
            )
            list.append(row)
        }
        if (items.isEmpty()) list.append(element("li", "empty-state", "Aucune panne attribuée."))
        null
    }.catch {
        list.innerHTML = ""; list.append(element("li", "empty-state", "Les pannes ne sont pas disponibles.")); null
    }
}

private fun renderLoadMore(session: dynamic, page: dynamic) {
    document.getElementById("load-more-maintenance")?.remove()
    val cursor = page.nextCursor as? String ?: return
    val more = element("button", text = "Charger la suite") as HTMLButtonElement
    more.id = "load-more-maintenance"; more.type = "button"
    more.onclick = {
        more.disabled = true
        fetchJson("maintenances/due?month=2026-08&cursor=" + js("encodeURIComponent")(cursor)).then<Any?> { next ->
            next.items = page.items.concat(next.items)
            renderReady(session, next)
            null
        }.catch { more.disabled = false; more.textContent = "R�essayer de charger la suite"; null }
        null
    }
    document.getElementById("maintenance-list")?.parentElement?.appendChild(more)
}

private fun renderMaintenanceList(items: Array<dynamic>) {
    val list = document.getElementById("maintenance-list") as? HTMLElement ?: return
    list.innerHTML = ""
    items.forEach { maintenance ->
        val id = maintenance.id as String
        val state = maintenance.state as String
        val dueDate = maintenance.dueDate as String
        val equipment = maintenance.equipment
        val button = element("button", "maintenance-card") as HTMLButtonElement
        button.type = "button"; button.setAttribute("aria-label", "Ouvrir ${equipment.displayNumber as String}"); button.onclick = { loadDetail(id); null }
        val dateTile = element("span", "date-tile"); dateTile.setAttribute("aria-label", "Échéance $dueDate"); dateTile.append(element("strong", text = day(dueDate)), element("small", text = month(dueDate)))
        val copy = element("span", "maintenance-copy")
        val topLine = element("span", "maintenance-topline"); topLine.append(element("strong", text = equipment.displayNumber as String), element("span", "state-badge ${stateClass(state)}", stateLabel(state)))
        copy.append(topLine, element("span", text = equipment.address as String), element("small", text = "${kindLabel(maintenance.kind as String)} · Secteur ${equipment.sectorId as String}"))
        button.append(dateTile, copy, element("span", "chevron", "›").apply { setAttribute("aria-hidden", "true") })
        element("li").apply { append(button); list.append(this) }
    }
}

private fun renderDetail(maintenance: dynamic) {
    currentMaintenance = maintenance
    pendingClaimId = null; selectedClaimPending = false; selectedOperations.clear(); textarea("public-note").value = ""; textarea("internal-note").value = ""
    val now = Date().toISOString().take(16); input("started-at").value = now; input("ended-at").value = now; input("signature-consent").checked = false
    text("detail-title", maintenance.equipment.displayNumber as String); text("detail-address", maintenance.equipment.address as String); text("detail-state", stateLabel(maintenance.state as String))
    text("detail-version", (maintenance.version as Number).toInt().toString()); text("detail-date", maintenance.dueDate as String); text("detail-kind", kindLabel(maintenance.kind as String)); text("local-save-state", "Pas encore enregistré")
    val policy = maintenance.completionPolicy
    val requirements = mutableListOf("opération", "durée")
    if ((policy.noteRequired as? Boolean) != false) requirements += "note"
    if ((policy.signatureRequired as? Boolean) == true) requirements += "signature"
    val minimum = (policy.minimumDurationMinutes as? Number)?.toInt()
    text("completion-policy-copy", requirements.joinToString(", ").replaceFirstChar { it.uppercase() } +
        " obligatoires" + (minimum?.let { " · $it minutes minimum" } ?: "") + ". Toute opération sécurité déclenche un contrôle serveur bloquant.")
    val serverDraft = maintenance.draft
    if (serverDraft != null) applyDraft(serverDraft)
    renderOperations(maintenance.operations.unsafeCast<Array<dynamic>>())
    visible("detail-error", false); visible("detail-panel", true)
    offline().bindSignature("signature-canvas")
    loadLocalDetailState()
    refreshMedia()
}

private fun loadLocalDetailState() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    val maintenanceId = maintenance.id as String
    offline().listCommands(partition).unsafeCast<Promise<Array<dynamic>>>().then<Any?> { commands ->
        val claim = commands.firstOrNull { command -> command.maintenanceId == maintenanceId && command.commandType == "CLAIM_MAINTENANCE" && (command.localState == "pending" || command.localState == "sending" || command.localState == "failed") }
        selectedClaimPending = claim != null
        pendingClaimId = claim?.commandId as? String
        val conflict = commands.firstOrNull { command -> command.maintenanceId == maintenanceId && command.localState == "conflict" }
        visible("detail-conflict", conflict != null)
        if (conflict != null) text("conflict-copy", "Votre brouillon local est conservé. Version serveur : ${conflict.result?.problem?.serverVersion ?: "inconnue"}.")
        renderDetailActions()
        null
    }
    offline().loadDraft(partition, maintenanceId).unsafeCast<Promise<dynamic>>().then<Any?> { local ->
        if (local != null) { applyDraft(local.draft); text("local-save-state", "Sauvé localement"); renderOperations(maintenance.operations.unsafeCast<Array<dynamic>>()) }
        null
    }
}

private fun renderDetailActions() {
    val maintenance = currentMaintenance ?: return
    val state = maintenance.state as String
    val capabilities = maintenance.capabilities.unsafeCast<Array<String>>()
    val canClaim = state == "PLANNED" && !selectedClaimPending && capabilities.contains("maintenance.claim")
    val canEdit = selectedClaimPending || ((state == "CLAIMED" || state == "IN_PROGRESS") && capabilities.contains("maintenance.edit"))
    text("detail-state", if (selectedClaimPending) "Prise en charge locale" else stateLabel(state))
    visible("claim-zone", canClaim); visible("claim-pending", selectedClaimPending); visible("draft-form", canEdit); visible("safe-read", !canClaim && !selectedClaimPending && !canEdit)
}

private fun renderOperations(operations: Array<dynamic>) {
    val list = document.getElementById("operation-list") as? HTMLElement ?: return
    list.innerHTML = ""
    operations.forEach { operation ->
        val code = operation.code as String
        val checkbox = element("input") as HTMLInputElement
        checkbox.type = "checkbox"; checkbox.checked = selectedOperations.contains(code)
        checkbox.onchange = { if (checkbox.checked) selectedOperations.add(code) else selectedOperations.remove(code); saveLocalDraft(); null }
        val copy = element("span"); copy.append(element("strong", text = operation.label as String))
        if (operation.requiresSafetyAssessment as Boolean) copy.append(element("small", text = "Contrôle sécurité requis"))
        val label = element("label", "operation-choice"); label.append(checkbox, copy); list.append(label)
    }
}

private fun applyDraft(draft: dynamic) {
    selectedOperations.clear(); draft.operationCodes.unsafeCast<Array<String>>().forEach { selectedOperations.add(it) }
    textarea("public-note").value = draft.publicNote as? String ?: ""
    textarea("internal-note").value = draft.internalNote as? String ?: ""
    input("started-at").value = (draft.startedAt as? String)?.take(16) ?: input("started-at").value
    input("ended-at").value = (draft.endedAt as? String)?.take(16) ?: input("ended-at").value
}

private fun draftPayload(): dynamic {
    val draft = js("({})")
    draft.operationCodes = selectedOperations.sorted().toTypedArray()
    draft.publicNote = textarea("public-note").value
    draft.internalNote = textarea("internal-note").value.ifBlank { null }
    draft.startedAt = input("started-at").value.takeIf { it.isNotBlank() }?.let { Date(it).toISOString() }
    draft.endedAt = input("ended-at").value.takeIf { it.isNotBlank() }?.let { Date(it).toISOString() }
    draft.attachmentIds = emptyArray<String>()
    return draft
}

private fun draftExpectedVersion(): Int {
    val maintenance = currentMaintenance ?: return 0
    val current = (maintenance.version as Number).toInt()
    return current + if (selectedClaimPending && maintenance.state as String == "PLANNED") 1 else 0
}

private fun saveLocalDraft() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    offline().saveDraft(partition, maintenance.id as String, draftExpectedVersion(), draftPayload()).unsafeCast<Promise<String>>().then<Any?> { updatedAt ->
        text("local-save-state", "Sauvé à ${offline().localTime(updatedAt) as String}")
        null
    }
}

private fun queueClaim() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    offline().queueClaim(partition, maintenance.id as String, (maintenance.version as Number).toInt()).unsafeCast<Promise<dynamic>>().then<Any?> { command ->
        selectedClaimPending = true; pendingClaimId = command.commandId as String; renderDetailActions(); refreshOutbox(); showSyncMessage("Prise en charge enregistrée dans la file locale.", "success")
        if (isEffectivelyOnline()) syncOutbox()
        null
    }
}

private fun queueDraft() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    saveLocalDraft()
    val dependencies = pendingClaimId?.let { arrayOf(it) } ?: emptyArray()
    offline().queueDraft(partition, maintenance.id as String, draftExpectedVersion(), draftPayload(), dependencies).unsafeCast<Promise<dynamic>>().then<Any?> {
        refreshOutbox(); showSyncMessage("Brouillon enregistré et mis à synchroniser.", "success")
        if (isEffectivelyOnline()) syncOutbox()
        null
    }
}

private fun capturePhotos() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    offline().captureSelectedFiles("photo-input", partition, maintenance.id as String).unsafeCast<Promise<dynamic>>().then<Any?> {
        refreshMedia(); saveLocalDraft(); refreshRuntime(); showSyncMessage("Photos optimisées et conservées localement. Elles pourront reprendre après une coupure.", "success"); null
    }.catch { error ->
        val message = error.asDynamic().message as? String
        val copy = when {
            message?.contains("photo_limit_exceeded") == true -> "La limite est de 5 photos par maintenance."
            message?.contains("storage_quota_low") == true || message?.contains("QuotaExceededError") == true -> "Stockage insuffisant : synchronisez ou libérez de l’espace avant une nouvelle photo."
            message?.contains("media_decode_unsupported") == true -> "Ce fichier HEIC ne peut pas être décodé sur cet appareil. Utilisez la capture photo du navigateur."
            else -> "Format refusé : utilisez une image JPEG, PNG, WebP ou HEIC de 15 Mo maximum."
        }
        showSyncMessage(copy, "error")
        null
    }
}

private fun refreshRuntime() {
    val partition = currentPartition ?: return
    window.asDynamic().progiliftPwa.status(partition).unsafeCast<Promise<dynamic>>().then<Any?> { status ->
        val local = status.local
        val storage = local.storage
        val usage = ((storage.usage as? Number)?.toLong() ?: (storage.localBytes as Number).toLong())
        val quota = (storage.quota as? Number)?.toLong()
        text("runtime-version", "PWA ${status.runtimeVersion as String} · stockage ${(local.databaseVersion as Number).toInt()}")
        text("runtime-storage", "${formatSize(usage)} / ${quota?.let { formatSize(it) } ?: "quota inconnu"}")
        text("runtime-persistence", if (storage.persisted as Boolean) "Protégé" else "Standard")
        text("runtime-multitab", if (local.multiTab as Boolean) "Actif" else "Secours")
        val updateReady = status.updateReady as Boolean
        text("runtime-badge", if (updateReady) "Mise à jour prête" else "À jour")
        document.getElementById("runtime-badge")?.className = "runtime-badge${if (updateReady) " ready" else ""}"
        visible("runtime-activate", updateReady)
        (document.getElementById("runtime-activate") as? HTMLButtonElement)?.disabled = syncing || currentMaintenance != null
        (document.getElementById("runtime-persist") as? HTMLButtonElement)?.disabled = storage.persisted as Boolean
        if (storage.evictionSuspected as Boolean) {
            text("runtime-message", "Le stockage IndexedDB semble avoir été évincé. Rechargez les données serveur avant de conclure que la file est vide.")
            visible("runtime-message", true)
        }
        null
    }.catch { text("runtime-message", "Le diagnostic PWA n’est pas disponible dans ce navigateur."); visible("runtime-message", true); null }
}

private fun checkRuntimeUpdate() {
    window.asDynamic().progiliftPwa.checkForUpdate().unsafeCast<Promise<dynamic>>().then<Any?> {
        refreshRuntime(); text("runtime-message", "Vérification terminée. Une version en attente ne s’active jamais pendant une saisie."); visible("runtime-message", true); null
    }.catch { text("runtime-message", "La vérification de version a échoué. Réessayez avec le réseau."); visible("runtime-message", true); null }
}

private fun requestPersistentStorage() {
    offline().requestPersistentStorage().unsafeCast<Promise<Boolean>>().then<Any?> { persisted ->
        refreshRuntime(); text("runtime-message", if (persisted) "Le navigateur a accordé la persistance du stockage local." else "Le navigateur conserve sa politique standard d’éviction."); visible("runtime-message", true); null
    }
}

private fun activateRuntimeUpdate() {
    val partition = currentPartition ?: return
    window.asDynamic().progiliftPwa.activateUpdate(partition, syncing || currentMaintenance != null).unsafeCast<Promise<dynamic>>().then<Any?> { result ->
        if (!(result.activated as Boolean)) {
            text("runtime-message", if (result.reason as? String == "work_in_progress") "Fermez la saisie et terminez la synchronisation avant l’activation." else "La mise à jour reste en attente, sans toucher au travail local.")
            visible("runtime-message", true)
        }
        null
    }
}

private fun captureSignature() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    if (!input("signature-consent").checked) return
    offline().captureSignature("signature-canvas", partition, maintenance.id as String, "TECHNICIAN_SIGNATURE").unsafeCast<Promise<dynamic>>().then<Any?> {
        refreshMedia(); showSyncMessage("Signature datée et conservée localement.", "success"); null
    }.catch { showSyncMessage("Tracez votre signature avant de la conserver.", "error"); null }
}

private fun refreshMedia() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    offline().listMedia(partition, maintenance.id as String).unsafeCast<Promise<Array<dynamic>>>().then<Any?> { media ->
        val photos = media.count { it.kind as String == "PHOTO" }
        text("photo-count", "$photos/5")
        input("photo-input").disabled = photos >= 5
        document.getElementById("media-picker")?.className = "media-picker${if (photos >= 5) " disabled" else ""}"
        visible("sync-media-button", media.any { it.localState as String != "ready" })
        val required = (currentMaintenance?.completionPolicy?.signatureRequired as? Boolean) == true
        text("signature-status", if (media.any { it.kind as String == "TECHNICIAN_SIGNATURE" }) "Enregistrée" else if (required) "Obligatoire" else "Non requise")
        renderMedia(media)
        null
    }
}

private fun renderMedia(media: Array<dynamic>) {
    val list = document.getElementById("media-list") as? HTMLElement ?: return
    list.innerHTML = ""
    visible("media-list", media.isNotEmpty())
    media.forEach { item ->
        val row = element("li")
        row.append(element("span", "media-icon", if (item.kind as String == "PHOTO") "▧" else "✎").apply { setAttribute("aria-hidden", "true") })
        val copy = element("div")
        val state = item.localState as String
        val progressValue = (item.progress as Number).toInt()
        copy.append(element("strong", text = item.fileName as String), element("small", text = "${mediaStateLabel(state, progressValue)} · ${formatSize((item.size as Number).toLong())}"))
        val progress = element("progress") as HTMLProgressElement; progress.max = 100.0; progress.value = progressValue.toDouble(); copy.append(progress)
        val remove = element("button", "media-remove", "×") as HTMLButtonElement
        remove.type = "button"; remove.disabled = state == "uploading"; remove.setAttribute("aria-label", "Retirer ${item.fileName as String}")
        remove.onclick = { offline().removeMedia(currentPartition, item.mediaId as String).unsafeCast<Promise<dynamic>>().then<Any?> { refreshMedia(); saveLocalDraft(); null }; null }
        row.append(copy, remove); list.append(row)
    }
}

private fun syncSelectedMedia() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    val csrf = currentCsrfToken ?: return
    if (!isEffectivelyOnline() || syncing) return
    syncing = true; refreshOutbox()
    offline().syncMedia(partition, maintenance.id as String, apiBaseUrl(), csrf, null).unsafeCast<Promise<dynamic>>().then<Any?> {
        syncing = false; refreshMedia(); refreshOutbox(); saveLocalDraft(); showSyncMessage("Médias vérifiés par empreinte et rattachables à la clôture.", "success"); null
    }.catch { syncing = false; refreshMedia(); refreshOutbox(); showSyncMessage("Envoi interrompu : les blocs reçus sont conservés et seront repris.", "warning"); null }
}

private fun queueCompletion() {
    val partition = currentPartition ?: return
    val maintenance = currentMaintenance ?: return
    val started = input("started-at").value
    val ended = input("ended-at").value
    if (selectedOperations.isEmpty() || textarea("public-note").value.isBlank() || started.isBlank() || ended.isBlank() || ended < started) {
        showSyncMessage("Complétez une opération, la note et une durée cohérente avant la clôture.", "error"); return
    }
    offline().attachmentSelection(partition, maintenance.id as String).unsafeCast<Promise<dynamic>>().then<Any?> { selection ->
        if (!(selection.allReady as Boolean)) {
            showSyncMessage(if (isEffectivelyOnline()) "Envoyez d’abord tous les médias, puis relancez la clôture." else "Clôture préparée localement : les médias devront être envoyés au retour du réseau.", "warning")
            saveLocalDraft(); return@then null
        }
        val draft = draftPayload(); draft.attachmentIds = selection.attachmentIds
        val claimDependencies = pendingClaimId?.let { arrayOf(it) } ?: emptyArray()
        offline().queueDraft(partition, maintenance.id as String, draftExpectedVersion(), draft, claimDependencies).unsafeCast<Promise<dynamic>>().then<Any?> { draftCommand ->
            offline().queueCompletion(
                partition, maintenance.id as String, draftExpectedVersion() + 1,
                maintenance.completionPolicy.policyVersion as String, draft, selection.signatureAttachmentId,
                arrayOf(draftCommand.commandId as String)
            ).unsafeCast<Promise<dynamic>>().then<Any?> {
                refreshOutbox(); showSyncMessage("Clôture préparée. Elle ne sera définitive qu’après validation du serveur.", "success")
                if (isEffectivelyOnline()) syncOutbox()
                null
            }
            null
        }
        null
    }
}

private fun toggleOffline() {
    forceOffline = !forceOffline
    text("offline-toggle", if (forceOffline) "Rétablir le réseau" else "Simuler hors ligne")
    renderConnectivity()
    showSyncMessage(if (forceOffline) "Mode hors ligne simulé : les actions restent sur cet appareil." else "Réseau rétabli : la file va être rejouée.", if (forceOffline) "warning" else "success")
    if (!forceOffline && isEffectivelyOnline() && pendingCount > 0) syncOutbox()
}

private fun refreshOutbox() {
    val partition = currentPartition ?: return
    offline().summary(partition).unsafeCast<Promise<dynamic>>().then<Any?> { summary ->
        pendingCount = (summary.pending as Number).toInt(); conflictCount = (summary.conflicts as Number).toInt(); mediaPendingCount = (summary.mediaPending as Number).toInt()
        text("sync-copy", if (pendingCount == 0) if (conflictCount == 0) "Tout le travail local est synchronisé" else "$conflictCount conflit(s) conservé(s)" else "$pendingCount action(s) en attente · $conflictCount conflit(s)")
        text("snapshot-pill", if (!isEffectivelyOnline()) "Copie locale" else if (pendingCount > 0) "À synchroniser" else "Synchronisé")
        visible("global-conflict", conflictCount > 0); if (conflictCount > 0) text("global-conflict-title", "$conflictCount conflit(s) à traiter")
        val syncButton = document.getElementById("sync-button") as? HTMLButtonElement
        syncButton?.disabled = syncing || !isEffectivelyOnline() || pendingCount == 0
        renderConnectivity()
        null
    }
    offline().listCommands(partition).unsafeCast<Promise<Array<dynamic>>>().then<Any?> { renderOutboxHistory(it); null }
}

private fun renderOutboxHistory(commands: Array<dynamic>) {
    visible("outbox-history", commands.isNotEmpty())
    text("outbox-total", commands.size.toString())
    val list = document.getElementById("outbox-list") as? HTMLElement ?: return
    list.innerHTML = ""
    commands.take(6).forEach { command ->
        val item = element("li")
        val label = when (command.commandType as String) { "CLAIM_MAINTENANCE" -> "Prise en charge"; "COMPLETE_MAINTENANCE" -> "Clôture"; else -> "Brouillon" }
        item.append(element("span", text = label), element("small", text = "${localStateLabel(command.localState as String)} · ${(command.commandId as String).take(8)}"))
        list.append(item)
    }
}

private fun renderConnectivity() {
    val online = isEffectivelyOnline()
    text("network-state", if (online) "Connecté" else "Hors ligne")
    document.getElementById("network-dot")?.className = "network-dot ${if (online) "online" else "offline"}"
}

private fun isEffectivelyOnline() = !forceOffline && (offline().online() as Boolean)

private fun syncOutbox() {
    val partition = currentPartition ?: return
    val csrf = currentCsrfToken ?: return
    if (syncing) return
    if (!isEffectivelyOnline()) { showSyncMessage("Aucun envoi : le travail reste protégé sur cet appareil.", "warning"); return }
    offline().acquireSyncLease(partition).unsafeCast<Promise<dynamic>>().then<Any?> { lease ->
        if (!(lease.acquired as Boolean)) {
            showSyncMessage("Un autre onglet synchronise déjà cette partition. La file reste observable ici.", "warning")
            return@then null
        }
        syncing = true; syncStartedAt = Date.now(); refreshOutbox()
        offline().syncAllMedia(partition, apiBaseUrl(), csrf).unsafeCast<Promise<dynamic>>().then<Any?> {
            refreshMedia()
            offline().prepareBatch(partition).unsafeCast<Promise<dynamic>>().then<Any?> { batch ->
                if (batch == null) { finishSync(partition, "La file locale est déjà à jour.", "success", "ok"); return@then null }
                val commands = batch.commands.unsafeCast<Array<dynamic>>()
                val commandIds = commands.map { it.commandId as String }.toTypedArray()
                offline().markBatchSending(partition, commandIds).unsafeCast<Promise<dynamic>>().then<Any?> {
                    val headers = Headers(); headers.set("X-CSRF-Token", csrf); headers.set("Idempotency-Key", batch.batchId as String)
                    apiFetch("sync/commands", "POST", JSON.stringify(batch), headers).then<Any?> { response ->
                        when {
                            response.status.toInt() == 401 -> { offline().releaseBatch(partition); lockAndShowLogin(); showAuthError("Votre session a expiré. Reconnectez-vous pour synchroniser le travail conservé."); finishSync(partition, null, null, "session_expired") }
                            !response.ok -> { offline().releaseBatch(partition); finishSync(partition, "La synchronisation a été refusée. La file est conservée.", "error", "http_${response.status}") }
                            else -> response.json().then<Any?> { result ->
                                val hasConflict = result.asDynamic().results.unsafeCast<Array<dynamic>>().any { it.state as String == "CONFLICT" }
                                offline().applyResults(partition, result.asDynamic().results).unsafeCast<Promise<dynamic>>().then<Any?> {
                                    finishSync(partition, if (hasConflict) "Synchronisation terminée avec un conflit conservé localement." else "Toutes les actions acceptées ont été synchronisées.", if (hasConflict) "warning" else "success", if (hasConflict) "conflict" else "ok")
                                    loadData(currentSession); null
                                }.catch {
                                    offline().releaseBatch(partition)
                                    finishSync(partition, "Réponse reçue mais non appliquée : la file locale reste disponible pour une nouvelle tentative.", "warning", "local_apply_error")
                                    null
                                }
                                null
                            }.catch {
                                offline().releaseBatch(partition)
                                finishSync(partition, "Réponse serveur illisible : la file locale reste disponible pour une nouvelle tentative.", "warning", "invalid_response")
                                null
                            }
                        }
                        null
                    }.catch { offline().releaseBatch(partition); finishSync(partition, "Réseau indisponible : aucune donnée locale n’a été perdue.", "warning", "network_error"); null }
                    null
                }.catch {
                    offline().releaseBatch(partition)
                    finishSync(partition, "Impossible de réserver le lot local : aucune action n’a été supprimée.", "warning", "local_mark_error")
                    null
                }
                null
            }.catch {
                finishSync(partition, "Impossible de préparer le lot local : aucune action n’a été supprimée.", "warning", "local_prepare_error")
                null
            }
            null
        }.catch { finishSync(partition, "Envoi média interrompu : la reprise repartira du dernier bloc confirmé.", "warning", "media_interrupted"); refreshMedia(); null }
        null
    }
}

private fun finishSync(partition: String, message: String?, state: String?, outcome: String) {
    syncing = false
    offline().releaseSyncLease(partition)
    offline().recordMetric(partition, "sync.duration.ms", Date.now() - syncStartedAt, "Kotlin:$outcome")
    if (message != null && state != null) showSyncMessage(message, state)
    refreshOutbox(); refreshRuntime()
}

private fun bindAdministration() {
    document.getElementById("admin-login-button")?.addEventListener("click", { loginAdministration() })
    document.getElementById("admin-save-policies")?.addEventListener("click", { saveAdministrationPolicies() })
    document.getElementById("admin-logout")?.addEventListener("click", { logoutAdministration() })
}

private fun restoreAdministration() {
    adminRequest("$adminMode/me").then<Any?> { response ->
        if (response.ok) response.json().then<Any?> { session ->
            adminCsrfToken = session.asDynamic().csrfToken as String
            loadAdministration(session)
            null
        } else {
            visible("admin-loading", false); visible("admin-login", true)
            null
        }
        null
    }.catch { visible("admin-loading", false); visible("admin-login", true); null }
}

private fun loginAdministration() {
    visible("admin-error", false)
    val identity = input("admin-identity").value.trim()
    val password = input("admin-password").value
    if (identity.isBlank() || password.isBlank()) {
        text("admin-error", "Renseignez votre identifiant et votre mot de passe."); visible("admin-error", true); return
    }
    val payload = js("({})")
    if (adminMode == "super-admin") payload.login = identity else payload.company = identity
    payload.password = password
    adminRequest("$adminMode/auth/sessions", "POST", JSON.stringify(payload)).then<Any?> { response ->
        if (response.ok) response.json().then<Any?> { session ->
            adminCsrfToken = session.asDynamic().csrfToken as String
            input("admin-password").value = ""
            loadAdministration(session)
            null
        } else readProblem(response, "Identifiants invalides.").then<Any?> { message ->
            text("admin-error", message); visible("admin-error", true); null
        }
        null
    }.catch {
        text("admin-error", "L’administration est momentanément indisponible."); visible("admin-error", true); null
    }
}

private fun loadAdministration(session: dynamic) {
    visible("admin-loading", true); visible("admin-login", false); visible("admin-dashboard", false)
    if (adminMode == "super-admin") {
        adminJson("super-admin/companies").then<Any?> { companiesValue ->
            val companies = companiesValue.unsafeCast<Array<dynamic>>()
            val company = companies.firstOrNull()
            if (company == null) throw IllegalStateException("Aucune société raccordée")
            adminTenantId = company.tenantId as String
            text("admin-company", company.companyName as String)
            text("admin-session-label", "${session.login as String} · Super administrateur")
            loadAdministrationData("super-admin/companies/${company.tenantId}")
            null
        }.catch { showAdministrationLoadError(); null }
    } else {
        adminTenantId = session.tenantId as String
        text("admin-company", session.companyName as String)
        text("admin-session-label", "Accès société sécurisé")
        loadAdministrationData("company-admin")
    }
}

private fun loadAdministrationData(prefix: String) {
    adminJson("$prefix/policies").then<Any?> { policies ->
        adminPolicies = policies
        adminRevision = (policies.revision as Number).toInt()
        renderAdministrationPolicies(policies)
        adminJson("$prefix/technicians").then<Any?> { technicians ->
            renderAdministrationTechnicians(technicians.unsafeCast<Array<dynamic>>())
            visible("admin-loading", false); visible("admin-dashboard", true)
            null
        }
        null
    }.catch { showAdministrationLoadError(); null }
}

private fun showAdministrationLoadError() {
    visible("admin-loading", false); visible("admin-login", true)
    text("admin-error", "La configuration n’a pas pu être chargée."); visible("admin-error", true)
}

private fun renderAdministrationPolicies(policies: dynamic) {
    setPolicyInputs("breakdowns", policies.breakdowns)
    setPolicyInputs("maintenances", policies.maintenances)
    text("admin-revision", "Révision ${adminRevision}")
    visible("admin-save-message", false)
}

private fun setPolicyInputs(prefix: String, policy: dynamic) {
    input("$prefix-signature").checked = policy.signatureRequired as Boolean
    input("$prefix-note").checked = policy.noteRequired as Boolean
    input("$prefix-date").checked = policy.allowDateChange as Boolean
    input("$prefix-start").checked = policy.allowStartTimeChange as Boolean
    input("$prefix-end").checked = policy.allowEndTimeChange as Boolean
    input("$prefix-minimum-enabled").checked = policy.minimumDurationEnabled as Boolean
    input("$prefix-minimum").value = (policy.minimumDurationMinutes as Number).toInt().toString()
}

private fun renderAdministrationTechnicians(technicians: Array<dynamic>) {
    val list = document.getElementById("admin-technicians") as? HTMLElement ?: return
    list.innerHTML = ""
    technicians.forEach { technician ->
        val row = element("article", "admin-technician")
        val identity = element("span", "admin-technician-identity").apply {
            append(
                element("span", "avatar mini-avatar", initials(technician.displayName as String)),
                element("span").apply {
                    append(element("strong", text = technician.displayName as String), element("small", text = technician.code as String))
                }
            )
        }
        val select = element("select") as HTMLSelectElement
        select.append(element("option", text = "Technicien").apply { setAttribute("value", "TECHNICIAN") })
        select.append(element("option", text = "Responsable technique").apply { setAttribute("value", "TECHNICAL_MANAGER") })
        select.value = technician.role as String
        val roleButton = element("button", "secondary-button", "Enregistrer le rôle") as HTMLButtonElement
        roleButton.type = "button"
        roleButton.onclick = { saveTechnicianRole(technician, select, roleButton); null }
        val overrideButton = element("button", "text-button", if (technician.hasOverride as Boolean) "Retirer l’exception" else "Créer une exception") as HTMLButtonElement
        overrideButton.type = "button"
        overrideButton.onclick = { toggleTechnicianOverride(technician, !(technician.hasOverride as Boolean), overrideButton); null }
        val actions = element("span", "admin-technician-actions").apply {
            append(select, roleButton)
            if (adminMode != "super-admin") append(overrideButton)
        }
        row.append(identity, actions)
        list.append(row)
    }
    if (technicians.isEmpty()) list.append(element("p", "empty-state", "Aucun technicien raccordé."))
}

private fun currentPolicy(prefix: String): dynamic {
    val policy = js("({})")
    policy.signatureRequired = input("$prefix-signature").checked
    policy.allowDateChange = input("$prefix-date").checked
    policy.allowStartTimeChange = input("$prefix-start").checked
    policy.allowEndTimeChange = input("$prefix-end").checked
    policy.minimumDurationEnabled = input("$prefix-minimum-enabled").checked
    policy.minimumDurationMinutes = input("$prefix-minimum").value.toIntOrNull() ?: 1
    policy.noteRequired = input("$prefix-note").checked
    return policy
}

private fun saveAdministrationPolicies() {
    val payload = js("({})")
    payload.expectedRevision = adminRevision
    payload.breakdowns = currentPolicy("breakdowns")
    payload.maintenances = currentPolicy("maintenances")
    val prefix = administrationDataPrefix()
    adminMutation("$prefix/policies", JSON.stringify(payload)).then<Any?> { policies ->
        adminPolicies = policies
        adminRevision = (policies.revision as Number).toInt()
        renderAdministrationPolicies(policies)
        text("admin-save-message", "Configuration enregistrée. Elle sera appliquée à la prochaine synchronisation.")
        visible("admin-save-message", true)
        null
    }.catch { text("admin-save-message", "Enregistrement refusé. Rechargez la configuration."); visible("admin-save-message", true); null }
}

private fun saveTechnicianRole(technician: dynamic, select: HTMLSelectElement, button: HTMLButtonElement) {
    button.disabled = true
    val payload = js("({})")
    payload.expectedRevision = adminRevision
    payload.technicianCode = technician.code
    payload.displayName = technician.displayName
    payload.role = select.value
    val prefix = administrationDataPrefix()
    adminMutation("$prefix/technicians/${technician.id as String}/role", JSON.stringify(payload)).then<Any?> { policies ->
        adminRevision = (policies.revision as Number).toInt(); adminPolicies = policies
        text("admin-revision", "Révision $adminRevision"); button.disabled = false; null
    }.catch { button.disabled = false; null }
}

private fun toggleTechnicianOverride(technician: dynamic, enabled: Boolean, button: HTMLButtonElement) {
    button.disabled = true
    val payload = js("({})")
    payload.expectedRevision = adminRevision
    payload.technicianCode = technician.code
    payload.displayName = technician.displayName
    payload.enabled = enabled
    payload.breakdowns = currentPolicy("breakdowns")
    payload.maintenances = currentPolicy("maintenances")
    val prefix = administrationDataPrefix()
    adminMutation("$prefix/technicians/${technician.id as String}/policies", JSON.stringify(payload)).then<Any?> { policies ->
        adminRevision = (policies.revision as Number).toInt(); adminPolicies = policies
        technician.hasOverride = enabled
        button.textContent = if (enabled) "Retirer l’exception" else "Créer une exception"
        button.disabled = false; text("admin-revision", "Révision $adminRevision"); null
    }.catch { button.disabled = false; null }
}

private fun administrationDataPrefix(): String = if (adminMode == "super-admin") {
    "super-admin/companies/${adminTenantId ?: ""}"
} else "company-admin"

private fun logoutAdministration() {
    val headers = Headers()
    adminCsrfToken?.let { headers.set("X-CSRF-Token", it) }
    adminRequest("$adminMode/auth/sessions/current", "DELETE", headers = headers).then<Any?> {
        adminCsrfToken = null; adminTenantId = null; visible("admin-dashboard", false); visible("admin-login", true); null
    }.catch { visible("admin-dashboard", false); visible("admin-login", true); null }
}

private fun adminMutation(path: String, body: String): Promise<dynamic> {
    val headers = Headers()
    adminCsrfToken?.let { headers.set("X-CSRF-Token", it) }
    return adminRequest(path, "PUT", body, headers).then { response ->
        if (!response.ok) throw IllegalStateException("Mutation refusée (${response.status})")
        response.json()
    }
}

private fun adminJson(path: String): Promise<dynamic> = adminRequest(path).then { response ->
    if (!response.ok) throw IllegalStateException("Lecture refusée (${response.status})")
    response.json()
}

private fun adminRequest(path: String, method: String = "GET", body: String? = null, headers: Headers = Headers()): Promise<Response> {
    if (body != null) headers.set("Content-Type", "application/json")
    val init = js("({})")
    init.method = method; init.headers = headers; init.credentials = "include"; if (body != null) init.body = body
    return window.fetch("${apiBaseUrl()}/$path", init.unsafeCast<RequestInit>())
}

private fun showSyncMessage(message: String, state: String) {
    val target = document.getElementById("sync-message") as? HTMLElement ?: return
    target.textContent = message; target.className = "sync-message $state"; target.hidden = false
}

private fun fetchJson(path: String): Promise<dynamic> = apiFetch(path).then { response -> if (!response.ok) throw IllegalStateException("Lecture refusée (${response.status}).") else response.json() }

private fun apiFetch(path: String, method: String = "GET", body: String? = null, headers: Headers = Headers()): Promise<Response> {
    val apiBase = apiBaseUrl()
    if (body != null) headers.set("Content-Type", "application/json")
    val init = js("({})"); init.method = method; init.headers = headers; init.credentials = "include"; if (body != null) init.body = body
    return window.fetch("$apiBase/$path", init.unsafeCast<RequestInit>())
}

private fun apiBaseUrl() = (document.querySelector("meta[name='api-base-url']") as? HTMLMetaElement)?.content?.trimEnd('/') ?: "http://127.0.0.1:5201/api/v1"

private fun readProblem(response: Response, fallback: String): Promise<String> = response.json().then { problem -> (problem.asDynamic().title as? String) ?: fallback }

private fun showAuth(step: String) {
    text("hero-eyebrow", "Accès technicien"); text("hero-title", "Votre espace sécurisé"); text("session-copy", "Validation en deux étapes par e-mail")
    listOf("loading-state", "error-state", "ready-state").forEach { visible(it, false) }
    visible("auth-state", true); document.getElementById("auth-state")?.setAttribute("aria-labelledby", if (step == "code") "auth-code-title" else "auth-title")
    visible("credentials-step", step == "credentials"); visible("code-step", step == "code")
    document.getElementById("step-one")?.className = if (step == "credentials") "active" else "done"; document.getElementById("step-two")?.className = if (step == "code") "active" else ""
    if (step == "credentials") { challengeId = null; clearAuthError() }
    hideDetail()
}

private fun showOnly(stateId: String) {
    listOf("auth-state", "loading-state", "error-state", "ready-state").forEach { visible(it, it == stateId) }
    if (stateId != "ready-state") hideDetail()
}

private fun submitting(value: Boolean) {
    listOf("login-button", "verify-button", "back-button", "resend-button").forEach { id -> (document.getElementById(id) as? HTMLButtonElement)?.disabled = value || id == "resend-button" }
}

private fun showAuthError(message: String) { text("auth-error", message); visible("auth-error", true) }
private fun clearAuthError() { visible("auth-error", false) }
private fun hideDetail() { visible("detail-panel", false); currentMaintenance = null }
private fun visible(id: String, value: Boolean) { (document.getElementById(id) as? HTMLElement)?.hidden = !value }
private fun input(id: String) = document.getElementById(id) as HTMLInputElement
private fun textarea(id: String) = document.getElementById(id) as HTMLTextAreaElement
private fun text(id: String, value: String) { document.getElementById(id)?.textContent = value }
private fun element(tag: String, className: String = "", text: String? = null): Element = document.createElement(tag).also { if (className.isNotEmpty()) it.className = className; if (text != null) it.textContent = text }
private fun remainingAttemptsText() = "$challengeRemainingAttempts tentative(s) disponible(s)."
private fun initials(name: String) = name.split(' ').filter { it.isNotBlank() }.joinToString("") { it.first().toString() }.uppercase()
private fun day(date: String) = if (date.length >= 10) date.substring(8, 10) else date
private fun month(date: String) = if (date.length >= 7) when (date.substring(5, 7)) { "08" -> "AOÛT"; "09" -> "SEPT."; else -> date.substring(5, 7) } else ""
private fun kindLabel(kind: String) = if (kind == "MONTHLY") "Mensuelle" else "Périodique"
private fun stateLabel(state: String) = when (state) { "PLANNED" -> "À faire"; "CLAIMED" -> "Réservée"; "IN_PROGRESS" -> "En cours"; "COMPLETED" -> "Clôturée"; else -> state }
private fun operationalState(state: String) = when (state) { "REPORTED", "NEW", "TO_TAKE", "ASSIGNED" -> "À prendre"; "CLAIMED", "TO_HANDLE" -> "À traiter"; "IN_PROGRESS", "WAITING" -> "En cours"; "EQUIPMENT_STOPPED", "STOPPED" -> "À l’arrêt"; "COMPLETED" -> "Clôturée"; else -> state }
private fun agendaTime(value: String) = if (value.length >= 16) value.substring(11, 16) else value
private fun stateClass(state: String) = state.lowercase().replace('_', '-')
private fun localStateLabel(state: String) = when (state) { "pending" -> "En attente"; "sending" -> "Envoi"; "acknowledged" -> "Acceptée"; "conflict" -> "Conflit"; "failed" -> "À réessayer"; else -> state }
private fun mediaStateLabel(state: String, progress: Int) = when (state) { "ready" -> "Vérifié"; "uploading" -> "Envoi $progress%"; "failed" -> "À reprendre $progress%"; else -> "Sur cet appareil" }
private fun formatSize(bytes: Long) = if (bytes >= 1_048_576) "${(bytes / 104_857.6).toInt() / 10.0} Mo" else "${maxOf(1, bytes / 1024)} Ko"

private fun administrationMarkup(superMode: Boolean): String {
    val title = if (superMode) "Super administration" else "Administration société"
    val subtitle = if (superMode) "Console centrale Progilift" else "Gestion de votre équipe technique"
    val identityLabel = if (superMode) "Identifiant administrateur" else "Nom de la société"
    val identityValue = if (superMode) "PROGILIFT" else "Société Démonstration"
    return """
      <main class="admin-app-shell">
        <header class="admin-brand"><a href="./" aria-label="Retour à l’application"><img src="brand/logo-progilift-blanc.svg?v=20260928-kotlin" alt="Progilift"></a><span>$title</span></header>
        <section id="admin-loading" class="state-panel"><span class="spinner" aria-hidden="true"></span><strong>Vérification de la session</strong></section>
        <section id="admin-login" class="admin-login-card" hidden>
          <span class="card-label">$title</span><h1>Bienvenue</h1><p>$subtitle</p>
          <div class="form-field"><label for="admin-identity">$identityLabel</label><input id="admin-identity" value="$identityValue" autocomplete="username"></div>
          <div class="form-field"><label for="admin-password">Mot de passe</label><input id="admin-password" type="password" value="Pilot!1" autocomplete="current-password"></div>
          <button id="admin-login-button" class="primary-button full-button" type="button">Ouvrir l’administration</button>
          <p id="admin-error" class="auth-error" role="alert" hidden></p>
          <a class="admin-back-link" href="./">← Retour à l’espace technicien</a>
        </section>
        <section id="admin-dashboard" class="admin-dashboard" hidden>
          <header class="admin-dashboard-header"><div><span class="card-label">Configuration de l’application</span><h1 id="admin-company"></h1><small id="admin-session-label"></small></div><button id="admin-logout" class="logout-button" type="button">Déconnexion</button></header>
          <div class="admin-heading"><div><h2>Droits et obligations</h2><p>Définissez les règles appliquées aux techniciens lors de leurs interventions.</p></div><span id="admin-revision" class="snapshot-pill"></span></div>
          <div id="admin-save-message" class="runtime-message" role="status" hidden></div>
          <div class="admin-policy-grid">
            ${administrationPolicyMarkup("breakdowns", "Pannes", "Règles de traitement et de validation d’une panne.")}
            ${administrationPolicyMarkup("maintenances", "Maintenances", "Règles de réalisation et de validation d’une maintenance.")}
          </div>
          <button id="admin-save-policies" class="primary-button admin-save" type="button">Enregistrer les règles générales</button>
          <section class="admin-team-card"><div class="section-heading"><div><span class="card-label">Équipe</span><h2>Techniciens et rôles</h2></div></div><div id="admin-technicians" class="admin-technician-list"></div></section>
        </section>
      </main>
    """.trimIndent()
}

private fun administrationPolicyMarkup(prefix: String, title: String, description: String) = """
  <section class="admin-policy-card">
    <div><span class="operational-icon" aria-hidden="true">${title.first()}</span><span><h3>$title</h3><p>$description</p></span></div>
    <label class="admin-switch"><input id="$prefix-signature" type="checkbox"><span>Signature obligatoire</span></label>
    <label class="admin-switch"><input id="$prefix-note" type="checkbox"><span>Note obligatoire</span></label>
    <label class="admin-switch"><input id="$prefix-date" type="checkbox"><span>Autoriser le changement de date</span></label>
    <label class="admin-switch"><input id="$prefix-start" type="checkbox"><span>Autoriser l’heure de début</span></label>
    <label class="admin-switch"><input id="$prefix-end" type="checkbox"><span>Autoriser l’heure de fin</span></label>
    <label class="admin-switch"><input id="$prefix-minimum-enabled" type="checkbox"><span>Durée minimale active</span></label>
    <label class="form-field compact-field"><span>Durée minimale (minutes)</span><input id="$prefix-minimum" type="number" min="1" max="480"></label>
  </section>
""".trimIndent()

private fun shellMarkup() = """
    <main class="app-shell">
      <header class="brand-hero"><div class="brand-row"><img src="brand/logo-progilift-blanc.svg?v=20260928-kotlin" alt="Progilift"><span class="stack-pill">Kotlin · G7</span></div><p id="hero-eyebrow">Accès technicien</p><h1 id="hero-title">Votre espace sécurisé</h1><div class="session-status"><span aria-hidden="true"></span><span id="session-copy">Validation en deux étapes par e-mail</span></div></header>
      <section class="app-content" aria-live="polite">
        <div id="loading-state" class="state-panel" role="status"><span class="spinner" aria-hidden="true"></span><strong>Vérification de votre session</strong><small>Votre tournée sera restaurée automatiquement si la session est encore valide.</small></div>
        <section id="auth-state" class="auth-card" aria-labelledby="auth-title" hidden>
          <div class="auth-steps" aria-label="Progression de la connexion"><span id="step-one" class="active">1</span><i aria-hidden="true"></i><span id="step-two">2</span></div>
          <div id="credentials-step"><div class="auth-heading"><span class="card-label">Étape 1 sur 2</span><h2 id="auth-title">Connectez-vous</h2><p>Utilisez votre identifiant Progilift. Un code temporaire sera envoyé par e-mail.</p></div><div id="locked-work" class="locked-work" role="status" hidden><strong>Travail local conservé</strong><span id="locked-copy"></span></div><div class="form-field"><label for="identifier">Identifiant</label><input id="identifier" autocomplete="username" value="TECH_A"></div><div class="form-field"><label for="password">Mot de passe</label><input id="password" type="password" autocomplete="current-password" value="Pilot!1"><small>6 caractères minimum, avec chiffre, majuscule et caractère spécial.</small></div><button id="login-button" class="primary-button auth-submit" type="button">Recevoir mon code</button><div class="pilot-hint"><strong>Accès pilote local</strong><span>TECH_A · Pilot!1</span></div><div class="admin-entry-links"><a href="?view=administration">Administration société</a><a href="?view=super-administration">Console Progilift</a></div></div>
          <div id="code-step" hidden><div class="auth-heading"><span class="card-label">Étape 2 sur 2</span><h2 id="auth-code-title">Vérifiez votre e-mail</h2><p>Le code envoyé à <strong id="masked-destination"></strong> reste valable 10 minutes.</p></div><div class="form-field code-field"><label for="mfa-code">Code à 6 chiffres</label><input id="mfa-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6"><small id="remaining-attempts"></small></div><button id="verify-button" class="primary-button auth-submit" type="button">Valider et ouvrir ma session</button><div class="auth-actions"><button id="back-button" class="text-button" type="button">Changer d’identifiant</button><button id="resend-button" class="text-button" type="button" disabled>Renvoyer le code</button></div><div id="capture-hint" class="pilot-hint success-hint" hidden><strong>Mode local</strong><span>Le code capturé est 123456.</span></div></div>
          <div id="auth-error" class="auth-error" role="alert" hidden></div>
        </section>
        <div id="error-state" class="state-panel error-panel" role="alert" hidden><span class="state-icon" aria-hidden="true">!</span><strong>Connexion momentanément indisponible</strong><small>Vérifiez que l’API locale est démarrée, puis relancez la lecture.</small><button id="retry-button" class="primary-button" type="button">Réessayer</button></div>
        <div id="ready-state" hidden>
          <section class="identity-card" aria-labelledby="identity-title"><div id="identity-initials" class="avatar" aria-hidden="true"></div><div class="identity-copy"><span id="identity-title" class="card-label">Connecté pour</span><strong id="tenant-name"></strong><small id="sector-list"></small></div><button id="logout-button" class="logout-button" type="button">Déconnexion</button></section>
          <section class="sync-card" aria-labelledby="sync-title"><div class="sync-main"><span id="network-dot" class="network-dot online" aria-hidden="true"></span><div><span id="sync-title" class="card-label">Mode terrain</span><strong id="network-state">Connecté</strong><small id="sync-copy">Tout le travail local est synchronisé</small></div></div><div class="sync-actions"><button id="offline-toggle" class="secondary-button" type="button">Simuler hors ligne</button><button id="sync-button" class="primary-button compact-button" type="button" disabled>Synchroniser</button></div><div id="sync-message" class="sync-message" role="status" hidden></div><div id="global-conflict" class="conflict-banner" role="alert" hidden><strong id="global-conflict-title"></strong><span>Le brouillon local est conservé. Ouvrez la maintenance concernée pour le traiter.</span></div><details id="outbox-history" class="outbox-history" hidden><summary>Voir l’historique local (<span id="outbox-total">0</span>)</summary><ul id="outbox-list"></ul></details></section>
          <section class="runtime-card" aria-labelledby="runtime-title"><div class="runtime-heading"><div><span class="card-label">Application installable</span><strong id="runtime-title"><span id="runtime-version">PWA g7.0 · stockage 3</span></strong></div><span id="runtime-badge" class="runtime-badge">À jour</span></div><div class="runtime-grid"><span><strong id="runtime-storage">Mesure en cours</strong><small>Stockage local</small></span><span><strong id="runtime-persistence">Standard</strong><small>Persistance</small></span><span><strong id="runtime-multitab">Actif</strong><small>Multi-onglet</small></span></div><div class="runtime-actions"><button id="runtime-check" class="secondary-button" type="button">Vérifier la version</button><button id="runtime-persist" class="secondary-button" type="button">Protéger le stockage</button></div><button id="runtime-activate" class="update-button" type="button" hidden>Activer hors saisie</button><div id="runtime-message" class="runtime-message" role="status" hidden></div></section>
          <section class="agenda-card" aria-labelledby="agenda-title"><div class="section-heading"><div><span class="card-label">Vendredi 25 septembre</span><h2 id="agenda-title">Mon agenda</h2></div><span class="snapshot-pill">Aujourd’hui</span></div><ul id="agenda-list" class="agenda-list"></ul><div id="agenda-empty" class="empty-state" hidden>Aucun rendez-vous aujourd’hui.</div></section>
          <section class="operational-grid" aria-label="Activités terrain">
            <button id="missions-card" class="operational-card mission-card" type="button"><span class="operational-icon" aria-hidden="true">M</span><span><small>Missions</small><strong><span id="missions-count">…</span> active(s)</strong><em>Réceptions et interventions planifiées</em></span><b aria-hidden="true">›</b></button>
            <button id="incidents-card" class="operational-card incident-card" type="button"><span class="operational-icon" aria-hidden="true">!</span><span><small>Incidents</small><strong><span id="incidents-count">…</span> active(s)</strong><em>Remises en service à traiter</em></span><b aria-hidden="true">›</b></button>
            <button id="breakdowns-card" class="operational-card breakdown-card" type="button"><span class="operational-icon" aria-hidden="true">⚠</span><span><small>Pannes</small><strong><span id="breakdowns-total">…</span> active(s)</strong><em id="breakdowns-copy">Chargement de la synthèse…</em></span><b aria-hidden="true">›</b></button>
          </section>
          <button id="supervision-entry" class="supervision-entry-card" type="button" hidden><span class="operational-icon" aria-hidden="true">S</span><span><strong>Mode supervision</strong><small>Suivre les pannes et la charge de l’équipe</small></span><b aria-hidden="true">›</b></button>
          <section class="maintenance-section" aria-labelledby="maintenance-title"><div class="section-heading"><div><span class="card-label">Aujourd’hui</span><h2 id="maintenance-title"><span id="maintenance-count"></span></h2></div><span id="snapshot-pill" class="snapshot-pill">Synchronisé</span></div><ul id="maintenance-list" class="maintenance-list"></ul><div id="empty-state" class="empty-state" hidden>Aucune maintenance dans votre périmètre.</div></section>
          <section id="operational-panel" class="detail-panel operational-panel" aria-labelledby="operational-title" hidden><div class="detail-heading"><div><span class="card-label">Activité terrain</span><h2 id="operational-title"></h2></div><button id="close-operational" class="icon-button" type="button" aria-label="Fermer la liste">×</button></div><ul id="operational-list" class="operational-list"></ul></section>
          <section id="detail-panel" class="detail-panel" aria-labelledby="detail-title" hidden>
            <div class="detail-heading"><div><span class="card-label">Intervention terrain</span><h2 id="detail-title"></h2></div><button id="close-detail" class="icon-button" type="button" aria-label="Fermer le détail">×</button></div><p id="detail-address"></p><dl class="detail-grid"><div><dt>État</dt><dd id="detail-state"></dd></div><div><dt>Version serveur</dt><dd id="detail-version"></dd></div><div><dt>Échéance</dt><dd id="detail-date"></dd></div><div><dt>Type</dt><dd id="detail-kind"></dd></div></dl>
            <div id="detail-conflict" class="conflict-banner detail-conflict" role="alert" hidden><strong>Le serveur a une version plus récente</strong><span id="conflict-copy"></span></div>
            <div id="claim-zone" class="action-zone" hidden><span class="card-label">Action explicite</span><strong>Prendre en charge cette maintenance</strong><p>La consultation seule ne la réserve jamais. Cette action peut être mise en file hors ligne.</p><button id="claim-button" class="primary-button full-button" type="button">Prendre en charge</button></div>
            <div id="claim-pending" class="queued-note" hidden><span aria-hidden="true">↻</span><div><strong>Prise en charge en attente</strong><small>Elle sera rejouée avec le même identifiant de commande.</small></div></div>
            <form id="draft-form" class="draft-form" hidden><div class="form-section-heading"><div><span class="card-label">Brouillon local</span><h3>Compte rendu d’intervention</h3></div><span id="local-save-state" class="local-save-state">Pas encore enregistré</span></div><fieldset><legend>Opérations réalisées</legend><div id="operation-list"></div></fieldset><div class="form-field"><label for="public-note">Note d’intervention</label><textarea id="public-note" rows="4" placeholder="Décrivez l’intervention réalisée…"></textarea></div><div class="form-field"><label for="internal-note">Note interne <span>(facultatif)</span></label><textarea id="internal-note" rows="3" placeholder="Visible uniquement par les équipes internes"></textarea></div><div class="duration-grid"><div class="form-field"><label for="started-at">Début</label><input id="started-at" type="datetime-local"></div><div class="form-field"><label for="ended-at">Fin</label><input id="ended-at" type="datetime-local"></div></div><section class="media-section" aria-labelledby="media-title"><div class="media-heading"><div><span class="card-label">Médias hors ligne</span><h4 id="media-title">Photos <span id="photo-count">0/5</span></h4></div><span class="media-rule">JPEG · PNG · WebP · HEIC · 15 Mo max.</span></div><label id="media-picker" class="media-picker" for="photo-input"><span aria-hidden="true">＋</span><span><strong>Ajouter des photos</strong><small>Caméra ou galerie · conservées sur cet appareil</small></span></label><input id="photo-input" class="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" capture="environment" multiple><ul id="media-list" class="media-list" hidden></ul><button id="sync-media-button" class="secondary-button full-button" type="button" hidden>Envoyer ou reprendre les médias</button></section><section class="signature-section" aria-labelledby="signature-title"><div class="media-heading"><div><span class="card-label">Validation</span><h4 id="signature-title">Signature technicien</h4></div><span id="signature-status" class="signature-status">Selon la règle société</span></div><canvas id="signature-canvas" class="signature-canvas" width="680" height="180" aria-label="Zone de signature"></canvas><label class="consent-choice"><input id="signature-consent" type="checkbox"><span>Je confirme signer le compte rendu présenté.</span></label><div class="signature-actions"><button id="clear-signature-button" class="text-button" type="button">Effacer</button><button id="save-signature-button" class="secondary-button" type="button" disabled>Conserver la signature</button></div></section><div class="completion-policy"><strong>Prêt pour la clôture</strong><span id="completion-policy-copy">Les exigences sont calculées par le serveur.</span></div><div class="draft-policy"><span aria-hidden="true">i</span>Enregistrer le brouillon ne clôture jamais la maintenance.</div><button class="primary-button full-button" type="submit">Mettre le brouillon à synchroniser</button><button id="complete-button" class="complete-button full-button" type="button">Préparer et clôturer</button></form>
            <div id="safe-read" class="safe-read-note" hidden><span aria-hidden="true">✓</span>Consultation en lecture seule selon vos droits et l’état actuel.</div><div id="detail-error" class="detail-error" role="alert" hidden>Le détail n’est pas disponible. La liste n’a pas été modifiée.</div>
          </section>
        </div>
      </section>
    </main>
""".trimIndent()
